import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { BookableSlot } from './bookable-slot'
import { BookableSlotSerializer } from './bookable-slot.serializer'
import { Observable, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { groupBy } from 'src/app/shared/until'
import { productSelectors } from '../product/product.selectors'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'

@Injectable()
export class BookableSlotService extends ResourceService<BookableSlot> {
  constructor(
    private http: HttpClient,
    private bookableSlotSerializer: BookableSlotSerializer,
    private store: Store<AppState>
  ) {
    super(
      http,
      `${environment.backendApiUrl}/bookable-slots`,
      bookableSlotSerializer
    )
  }

  public enrichMap(bookableSlots: BookableSlot[]): Observable<BookableSlot[]> {
    return combineLatest([
      this.store.select(productSelectors.selectEntities)
    ]).pipe(
      map(([productEntities]) => {
        return Object.values(groupBy(bookableSlots, (x) => x.startDate))
          .reduce((slots, bookableSlotByStartDate: BookableSlot) => {
            const slotsByLinkedProductId: BookableSlot[][] = Object.values(
              groupBy(
                Object.values(bookableSlotByStartDate),
                (x) => x.linkedProductId ?? x.bookableProductId
              )
            )

            return [
              ...slots,
              ...slotsByLinkedProductId.reduce(
                (resultSlots, bookableSlots: BookableSlot[]) => {
                  const targetProduct =
                    productEntities[
                      bookableSlots[0]?.linkedProductId ??
                        bookableSlots[0]?.bookableProductId
                    ]

                  if (!targetProduct) {
                    console.warn(
                      'Got bookable slots without target product and will hide the slots',
                      bookableSlots
                    )
                    return resultSlots
                  }

                  // v12 depricated left in place now for previously configured products start
                  if (!targetProduct.showParentBookablesAsSingleResource) {
                    return [
                      ...resultSlots,
                      ...bookableSlots.map((b) => ({
                        ...b,
                        name: targetProduct.description,
                        group: targetProduct.id // used in schedule to form rows
                      }))
                    ]
                  } // v12 depricated left in place now for previously configured products end

                  const availableSlots = bookableSlots.filter(
                    (b) => b.isAvailable
                  )

                  if (!availableSlots?.length) {
                    return resultSlots
                  }

                  return [
                    ...resultSlots,
                    {
                      ...availableSlots[0],
                      name: targetProduct.description,
                      group: targetProduct.id, // used in schedule to form rows
                      groupCount: availableSlots.length,
                      availableOnParentBookableProductIds: availableSlots.map(
                        // used for preferred bookable product selection
                        (s) => s.bookableProductId
                      )
                    }
                  ]
                },
                []
              )
            ]
          }, [])
          .sort((a, b) => `${a.startDate}`.localeCompare(`${b.startDate}`))
      })
    )
  }
}
